import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MagnifyingGlass, FileText, Package as PackageIcon, User } from '@phosphor-icons/react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/dashboard-utils'
import type { Package, Proposal } from '@/lib/types'
import { useAppNavigate } from '@/hooks/useAppNavigate'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000'

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  })

  if (!response.ok && response.status === 401) {
    throw new Error('Unauthorized')
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  return response.json()
}

function mapPackageFromApi(pkg: Record<string, unknown>): Package {
  return {
    id: pkg.id as string,
    title: pkg.title as string,
    description: (pkg.description as string | null) ?? null,
    price_cents: (pkg.priceCents as number) ?? (pkg.price_cents as number) ?? 0,
    currency: pkg.currency as string,
    active: pkg.active as boolean,
    created_at: (pkg.createdAt as string) ?? (pkg.created_at as string) ?? new Date().toISOString(),
  }
}

export function GlobalSearchDialog({ className }: { className?: string }) {
  const { t } = useTranslation()
  const navigate = useAppNavigate()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [packages, setPackages] = useState<Package[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (proposals.length > 0 || packages.length > 0) return

    let canceled = false

    async function load() {
      try {
        setLoading(true)
        setError(null)

        const [proposalsData, packagesData] = await Promise.all([
          apiRequest<Proposal[]>('/api/proposals'),
          apiRequest<Record<string, unknown>[]>('/api/packages'),
        ])

        if (canceled) return

        setProposals(proposalsData)
        setPackages(packagesData.map(mapPackageFromApi).filter((p) => p.active))
      } catch (err: unknown) {
        if (canceled) return
        const message = err instanceof Error ? err.message : t('common:errors.generic')
        setError(message)
      } finally {
        if (!canceled) setLoading(false)
      }
    }

    load()
    return () => {
      canceled = true
    }
  }, [open, proposals.length, packages.length, t])

  const normalizedQuery = query.trim().toLowerCase()

  const proposalItems = useMemo(() => {
    return proposals
      .slice()
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .map((proposal) => {
        const searchable = [
          proposal.client_name,
          proposal.client_email ?? '',
          proposal.location ?? '',
          proposal.package?.title ?? '',
          proposal.status,
          proposal.event_date,
        ]
          .join(' ')
          .toLowerCase()

        return { proposal, searchable }
      })
      .filter(({ searchable }) => (normalizedQuery ? searchable.includes(normalizedQuery) : true))
      .slice(0, 20)
  }, [normalizedQuery, proposals])

  type ClientItem = {
    key: string
    name: string
    email: string | null
    phone: string | null
    count: number
    lastProposalId: string
    searchable: string
  }

  const clientItems = useMemo<ClientItem[]>(() => {
    const map = new Map<string, Omit<ClientItem, 'searchable'>>()

    for (const proposal of proposals) {
      const name = proposal.client_name
      const email = proposal.client_email ?? null
      const phone = proposal.client_phone ?? null

      const key = email ? `email:${email}` : `name:${name}|phone:${phone ?? ''}`

      const existing = map.get(key)
      if (!existing) {
        map.set(key, {
          key,
          name,
          email,
          phone,
          count: 1,
          lastProposalId: proposal.id,
        })
        continue
      }

      const existingLast = proposals.find((p) => p.id === existing.lastProposalId)
      const existingLastCreatedAt = existingLast ? new Date(existingLast.created_at).getTime() : 0
      const currentCreatedAt = new Date(proposal.created_at).getTime()

      map.set(key, {
        ...existing,
        count: existing.count + 1,
        lastProposalId: currentCreatedAt > existingLastCreatedAt ? proposal.id : existing.lastProposalId,
      })
    }

    const items = Array.from(map.values()).map((c) => {
      const searchable = [c.name, c.email ?? '', c.phone ?? ''].join(' ').toLowerCase()
      return { ...c, searchable }
    })

    return items
      .filter((c) => (normalizedQuery ? c.searchable.includes(normalizedQuery) : true))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20)
  }, [normalizedQuery, proposals])

  const packageItems = useMemo(() => {
    return packages
      .map((pkg) => {
        const searchable = [pkg.title, pkg.description ?? ''].join(' ').toLowerCase()
        return { pkg, searchable }
      })
      .filter(({ searchable }) => (normalizedQuery ? searchable.includes(normalizedQuery) : true))
      .slice(0, 20)
  }, [normalizedQuery, packages])

  const hasAnyResults = clientItems.length > 0 || proposalItems.length > 0 || packageItems.length > 0

  return (
    <>
      {/* Desktop: Input expandido */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'hidden xl:flex items-center gap-2 w-full max-w-sm px-3 py-2 rounded-xl',
          'bg-[var(--pd-muted)]/50 border border-[var(--pd-border)]',
          'text-sm text-[var(--pd-foreground)]/60 hover:bg-[var(--pd-muted)] hover:text-[var(--pd-foreground)]',
          'transition-colors cursor-text',
          className
        )}
      >
        <MagnifyingGlass size={18} className="text-[var(--pd-foreground)]/50" />
        <span className="flex-1 text-left">{t('common:search.placeholder')}</span>
        <kbd className="hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--pd-muted)] text-xs text-[var(--pd-foreground)]/50 font-mono">
          ⌘+K
        </kbd>
      </button>
      
      {/* Mobile: Botão de ícone */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        aria-label={t('common:search.title')}
        className={cn(
          'xl:hidden h-10 w-10 rounded-xl text-[var(--pd-foreground)] hover:bg-[var(--pd-muted)] transition-colors',
          className
        )}
      >
        <MagnifyingGlass size={20} />
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setQuery('')
        }}
        title={t('common:search.title')}
        description={t('common:search.description')}
      >
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder={t('common:search.placeholder')}
          autoFocus
        />
        <CommandList>
          {loading ? (
            <CommandEmpty>{t('common:loading')}</CommandEmpty>
          ) : error ? (
            <CommandEmpty>{error}</CommandEmpty>
          ) : !hasAnyResults ? (
            <CommandEmpty>{t('common:search.noResults')}</CommandEmpty>
          ) : null}

          {proposalItems.length > 0 ? (
            <CommandGroup heading={t('common:search.groups.proposals')}>
              {proposalItems.map(({ proposal }) => (
                <CommandItem
                  key={`proposal-${proposal.id}`}
                  value={`${proposal.client_name} ${proposal.client_email ?? ''} ${proposal.location ?? ''} ${proposal.package?.title ?? ''}`}
                  className="group/search-item"
                  onSelect={() => {
                    setOpen(false)
                    navigate(`proposals?proposalId=${encodeURIComponent(proposal.id)}`)
                  }}
                >
                  <FileText size={18} />
                  <div className="min-w-0 flex-1">
                    <div className="min-w-0 truncate font-medium">{proposal.client_name}</div>
                    <div className="min-w-0 truncate text-xs text-muted-foreground group-data-[selected=true]/search-item:text-[var(--pd-primary)]">
                      {proposal.package ? (
                        <>
                          {formatCurrency(proposal.package.price_cents, proposal.package.currency)}
                          {' • '}
                          {proposal.client_email ?? proposal.package.title}
                        </>
                      ) : (
                        (proposal.client_email ?? proposal.event_date)
                      )}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ) : null}

          {clientItems.length > 0 && proposalItems.length > 0 ? <CommandSeparator /> : null}

          {clientItems.length > 0 ? (
            <CommandGroup heading={t('common:search.groups.clients')}>
              {clientItems.flatMap((client) => {
                const label = client.email ?? client.phone ?? ''

                return [
                  <CommandItem
                    key={`client-create-${client.key}`}
                    value={`${client.name} ${client.email ?? ''} ${client.phone ?? ''} ${t('common:actions.createProposal')}`}
                    className="group/search-item"
                    onSelect={() => {
                      setOpen(false)
                      const params = new URLSearchParams()
                      params.set('create', '1')
                      params.set('clientName', client.name)
                      if (client.email) params.set('clientEmail', client.email)
                      if (client.phone) params.set('clientPhone', client.phone)
                      navigate(`proposals?${params.toString()}`)
                    }}
                  >
                    <User size={18} />
                    <div className="min-w-0 flex-1">
                      <div className="min-w-0 truncate font-medium">{client.name}</div>
                      <div className="min-w-0 truncate text-xs text-muted-foreground group-data-[selected=true]/search-item:text-[var(--pd-primary)]">
                        {t('common:actions.createProposal')}
                        {label ? ` • ${label}` : null}
                      </div>
                    </div>
                  </CommandItem>,
                  <CommandItem
                    key={`client-view-${client.key}`}
                    value={`${client.name} ${client.email ?? ''} ${client.phone ?? ''} ${t('common:actions.view')} ${t('common:navigation.proposals')}`}
                    className="group/search-item"
                    onSelect={() => {
                      setOpen(false)
                      navigate(`proposals?client=${encodeURIComponent(client.key)}`)
                    }}
                  >
                    <FileText size={18} />
                    <div className="min-w-0 flex-1">
                      <div className="min-w-0 truncate font-medium">{client.name}</div>
                      <div className="min-w-0 truncate text-xs text-muted-foreground group-data-[selected=true]/search-item:text-[var(--pd-primary)]">
                        {t('common:actions.view')} {t('common:navigation.proposals')}
                        {client.count > 0 ? ` • ${client.count}` : null}
                      </div>
                    </div>
                  </CommandItem>,
                ]
              })}
            </CommandGroup>
          ) : null}

          {(proposalItems.length > 0 || clientItems.length > 0) && packageItems.length > 0 ? <CommandSeparator /> : null}

          {packageItems.length > 0 ? (
            <CommandGroup heading={t('common:search.groups.packages')}>
              {packageItems.map(({ pkg }) => (
                <CommandItem
                  key={`package-${pkg.id}`}
                  value={`${pkg.title} ${pkg.description ?? ''}`}
                  className="group/search-item"
                  onSelect={() => {
                    setOpen(false)
                    navigate(`packages?packageId=${encodeURIComponent(pkg.id)}`)
                  }}
                >
                  <PackageIcon size={18} />
                  <div className="min-w-0 flex-1">
                    <div className="min-w-0 truncate font-medium">{pkg.title}</div>
                    {pkg.description ? (
                      <div className="min-w-0 truncate text-xs text-muted-foreground group-data-[selected=true]/search-item:text-[var(--pd-primary)]">
                        {pkg.description}
                      </div>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ) : null}
        </CommandList>
      </CommandDialog>
    </>
  )
}
